import React from 'react';

import { ExpandableTable, ChartDisplay } from '@quyva/shared-ui';
import { List } from 'antd';
import {
    ChatFormatType,
    isValidJsonString,
    handleHtmlStringToElement,
    renderAnswerStringToElement,
    DataType,
} from '@quyva/common';
import { getDate, getTime } from '@quyva/common';
import { FileDownloadList } from '@quyva/shared-ui';
import { ChatDebugModal } from '../chatDebugModal';
import { useAttachmentDownload } from '../../../hooks/useAttachmentDownload';

let pagination = false;
let answerDisplayComponent = null;

const getLimitationMessage = (rows) => {
    return `Only ${rows} data rows shown due to limitations.`;
};

export const AnswerContentView = ({
    chatObject,
    isSmallScreen,
    activeChart,
    setActiveChart,
    setEnableScrollToBottom,
    showDebugModal,
    setShowDebugModal,
    repoName,
}) => {
    const pageSize = isSmallScreen ? 3 : 5;
    // Files the backend produced alongside this answer (the Answer envelope's
    // `files`), rendered with the same buttons a question's attachments get.
    const { download, loadingPath } = useAttachmentDownload();

    if (chatObject) {
        // The backend now sends type="table" for DB results of 2+ rows and
        // type="string" for a single row, so database answers flow through
        // the same general mapping as every other repository type below.

        let answer = chatObject.answer;

        if (isValidJsonString(answer)) {
            let level = 0;
            // Unified value: the old DB branch parsed to depth 2 before the
            // database branch collapsed into this general path. DB scalar
            // answers now parse to the same depth as every other answer type.
            const validJsonLevels = 5;
            while (isValidJsonString(answer) && level < validJsonLevels) {
                answer = JSON.parse(answer);
                level++;
            }
        } else {
            answer = chatObject.answer;
        }
        if (chatObject.type === ChatFormatType.TABLE) {
            const columnData = chatObject.answer.tableHead;

            const tableData = chatObject.answer.tableData;
            // Rows arrive as arrays from the answer envelope (buildAnswerDisplay)
            // and as objects from /formatans, which genuinely returns row
            // objects. Object.values on an array row would also work, but going
            // through object keys is what corrupted envelope rows with duplicate
            // or integer-like column names, so arrays are used as they are.
            const tableFormattedData = tableData.map((rowData) =>
                Array.isArray(rowData) ? rowData : Object.values(rowData)
            );

            answerDisplayComponent = (
                <ExpandableTable
                    columnData={columnData}
                    tableData={tableFormattedData}
                    pageSize={pageSize}
                    pagination={pagination}
                    isSmallScreen={isSmallScreen}
                    extraContent={
                        chatObject.description
                            ? handleHtmlStringToElement(chatObject.description)
                            : null
                    }
                />
            );
        } else if (chatObject.type === ChatFormatType.CHART) {
            answerDisplayComponent = (
                <ChartDisplay
                    chatObject={chatObject}
                    activeChart={activeChart}
                    setActiveChart={setActiveChart}
                    isSmallScreen={isSmallScreen}
                    setEnableScrollToBottom={setEnableScrollToBottom}
                />
            );
        } else if (chatObject.type === ChatFormatType.LIST) {
            // Three parts, in the order the envelope encodes them:
            // `description` is the answer's prose lead-in (the envelope's
            // `text`), listTitle is data[0] - the heading the model gave the
            // list - and listData is data[1:], the actual points. The
            // description was previously dropped here, so the lead-in sentence
            // never reached the user. `list-disc ml-5` matches the convention
            // already used in sidebar.jsx; a bare <ul> renders without markers.
            answerDisplayComponent = (
                <>
                    {chatObject.description ? (
                        // html-react-parser returns a bare string for untagged
                        // text, which would sit inline against the heading -
                        // hence the block wrapper and the gap below it.
                        <div className='mb-2'>
                            {handleHtmlStringToElement(chatObject.description)}
                        </div>
                    ) : null}
                    {chatObject.answer?.listTitle ? (
                        <b className='block'>{chatObject.answer.listTitle}</b>
                    ) : null}
                    <ul className='list-disc ml-5'>
                        {(chatObject.answer?.listData ?? []).map((item, idx) => (
                            <li key={idx}>{item}</li>
                        ))}
                    </ul>
                </>
            );
        } else if (chatObject.type === ChatFormatType.MULTIPLE) {
            let title = '';
            let data = [];

            if (!Array.isArray(chatObject.answer)) {
                title = 'Multiple Answers';
                data = [chatObject.answer.toString()];
            } else {
                title = `${chatObject.answer.length} Answer${
                    chatObject.answer.length > 1 ? 's' : ''
                }`;
                data = chatObject.answer;

                if (chatObject.answer.length > 1) {
                    pagination = { pageSize: pageSize };
                }
            }

            answerDisplayComponent = (
                <List
                    header={
                        <div className='text-texts-secondary300 font-semibold text-center w-full'>
                            {title}
                        </div>
                    }
                    size='small'
                    dataSource={data}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                    pagination={pagination}
                />
            );
        } else if (chatObject.type === ChatFormatType.CUSTOM) {
            if (typeof chatObject.answer === ChatFormatType.STRING) {
                answerDisplayComponent = chatObject.answer;
            } else {
                answerDisplayComponent = chatObject.answer.toString();
            }
        } else if (answer && answer.type === ChatFormatType.TABLE) {
            const columnData = answer.payload.columns;
            const tableData = answer.payload.data;

            answerDisplayComponent = (
                <ExpandableTable
                    columnData={columnData}
                    tableData={tableData}
                    pageSize={pageSize}
                    pagination={pagination}
                    isSmallScreen={isSmallScreen}
                    extraContent={
                        answer.payload.completeData
                            ? null
                            : getLimitationMessage(tableData.length)
                    }
                />
            );
        } else {
            if (typeof chatObject.answer === DataType.STRING) {
                answerDisplayComponent = renderAnswerStringToElement(
                    chatObject.answer
                );
            } else {
                answerDisplayComponent = handleHtmlStringToElement(
                    chatObject.answer
                );
            }
        }

        return (
            <div className='w-full'>
                <div className='w-full text-slate-700 whitespace-pre-wrap leading-relaxed'>
                    {answerDisplayComponent}
                </div>
                <FileDownloadList
                    files={chatObject.answerFiles}
                    onDownload={download}
                    loadingPath={loadingPath}
                />
                <div className='w-full flex justify-between items-center text-xs text-texts-secondary300'>
                    <div>{getDate(chatObject.timestamp)}</div>
                    <div>{getTime(chatObject.timestamp)}</div>
                </div>
                {showDebugModal && (
                    <ChatDebugModal
                        setShowDebugModal={setShowDebugModal}
                        isSmallScreen={isSmallScreen}
                        chatObject={chatObject}
                        repoName={repoName}
                    />
                )}
            </div>
        );
    }
};
